import {useEffect, useRef, useState} from "react";
import {renderToStaticMarkup} from "react-dom/server";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
    clearSelectedDisplayDeliveryIdAction,
    deliveryState,
    setSelectedDisplayDeliveryIdAction,
} from "../../app/reducers/delivery-slice";
import {scheduleState} from "../../app/reducers/schedule-slice";
import {DeliveryGroupType} from "../../types/schedule/delivery-group-type";
import {DisplayDelivery} from "../../types/schedule/display-delivey";
import useDidMountEffect from "../../utils/custom-hook/use-did-mount-effect";
import {getMarkerFormat} from "../../utils/marker/marker-format";
import ScheduleCreatePin from "../pin/schedule-pin/ScheduleCreatePin";
import Point = naver.maps.Point;

interface CurrentMarker {
    locationId: string;
    index?: number;
}

let prevSelectedDeliveryMarkerIndex: number[] = [];

const ScheduleCreateMap = () => {
    const {displayDeliveries, selectedDisplayDeliveryId} =
        useAppSelector(deliveryState);
    const {preview} = useAppSelector(scheduleState);
    const dispatch = useAppDispatch();
    const [markers, setMarkers] = useState<naver.maps.Marker[]>([]);
    const [currentMarker, setCurrentMarker] = useState<CurrentMarker>({
        locationId: "",
        index: undefined,
    });
    const [markerDisplayDeliveries, setMarkerdisplayDeliveries] = useState<
        DisplayDelivery[]
    >([]);
    const [markerDisplayDeliveriesType, setMarkerdisplayDeliveriesType] =
        useState<DeliveryGroupType[]>([]);

    const mapRef = useRef<HTMLElement | null | any>(null);

    const displayMarker = () => {
        const newMarkers: naver.maps.Marker[] = [];
        const markersLocationId: string[] = [];
        const newMarkerDisplayDeliveries: DisplayDelivery[] = [];
        const markerDisplayDeliveriesType: DeliveryGroupType[] = [];

        if (markers) {
            markers.forEach((marker) => marker.setMap(null));
        }

        if (displayDeliveries) {
            displayDeliveries.map((displayDelivery, displayDeliveryIndex) => {
                displayDelivery.deliveries.map((delivery, deliveryIndex) => {
                    if (deliveryIndex === 0) {
                        const COORDINATE_CORRECTION = (Math.random() - 0.5) * 0.00005;
                        if (displayDelivery.type === DeliveryGroupType.PickUp) {
                            newMarkerDisplayDeliveries.push(displayDelivery);
                            markerDisplayDeliveriesType.push(displayDelivery.type);
                            const markerFormat = getMarkerFormat(
                                displayDelivery.deliveries,
                                displayDelivery.locationId,
                                displayDelivery.type
                            );
                            markersLocationId.push(
                                displayDelivery.deliveries[0].pickUpLocation.id +
                                displayDelivery.type
                            );
                            const marker = new naver.maps.Marker({
                                position: new naver.maps.LatLng(
                                    displayDelivery.deliveries[0].pickUpLocation.latitude -
                                    COORDINATE_CORRECTION,
                                    displayDelivery.deliveries[0].pickUpLocation.longitude -
                                    COORDINATE_CORRECTION
                                ),
                                map: mapRef.current,
                                icon: {
                                    content: renderToStaticMarkup(
                                        <ScheduleCreatePin
                                            type={markerFormat.type}
                                            timeRange={markerFormat.timeRange}
                                            aliasLabel={markerFormat.aliasLabel}
                                            totalDeliveries={markerFormat.totalDeliveries}
                                            checkedDeliveries={markerFormat.checkedDeliveries}
                                            focus={markerFormat.focus}
                                            deliveryGroupOrder={markerFormat.deliveryGroupOrder}
                                        />
                                    ),
                                    anchor: new Point(1.0, 0.5)
                                },
                            });
                            newMarkers.push(marker);
                        } else {
                            newMarkerDisplayDeliveries.push(displayDelivery);
                            markerDisplayDeliveriesType.push(displayDelivery.type);
                            const markerFormat = getMarkerFormat(
                                displayDelivery.deliveries,
                                displayDelivery.locationId,
                                displayDelivery.type
                            );
                            markersLocationId.push(
                                displayDelivery.deliveries[0].dropOffLocation.id +
                                displayDelivery.type
                            );
                            const marker = new naver.maps.Marker({
                                position: new naver.maps.LatLng(
                                    displayDelivery.deliveries[0].dropOffLocation.latitude -
                                    COORDINATE_CORRECTION,
                                    displayDelivery.deliveries[0].dropOffLocation.longitude -
                                    COORDINATE_CORRECTION
                                ),
                                map: mapRef.current,
                                icon: {
                                    content: renderToStaticMarkup(
                                        <ScheduleCreatePin
                                            type={markerFormat.type}
                                            timeRange={markerFormat.timeRange}
                                            aliasLabel={markerFormat.aliasLabel}
                                            totalDeliveries={markerFormat.totalDeliveries}
                                            checkedDeliveries={markerFormat.checkedDeliveries}
                                            focus={markerFormat.focus}
                                            deliveryGroupOrder={markerFormat.deliveryGroupOrder}
                                        />
                                    ),
                                    anchor: new Point(0.5, 1.0)
                                },
                            });
                            newMarkers.push(marker);
                        }
                    }
                });
            });
        }

        const getClickHandler = (seq: number) => {
            return () => {
                const marker = newMarkers[seq];

                if (selectedDisplayDeliveryId === markersLocationId[seq]) {
                    setCurrentMarker({
                        locationId: "",
                        index: undefined,
                    });
                    dispatch(clearSelectedDisplayDeliveryIdAction());
                } else {
                    setCurrentMarker({
                        locationId: markersLocationId[seq],
                        index: seq,
                    });
                    dispatch(
                        setSelectedDisplayDeliveryIdAction({id: markersLocationId[seq]})
                    );
                }
            };
        };

        for (let i = 0; i < newMarkers.length; i += 1) {
            naver.maps.Event.addListener(newMarkers[i], "click", getClickHandler(i));
        }

        setMarkerdisplayDeliveriesType(markerDisplayDeliveriesType);
        setMarkerdisplayDeliveries(newMarkerDisplayDeliveries);
        setMarkers(newMarkers);
    };

    useEffect(() => {
        const initMap = () => {
            mapRef.current = new naver.maps.Map("map", {
                center: new naver.maps.LatLng(37.530791, 126.9795728),
                zoom: 12,
            });
        };
        initMap();
    }, []);

    useDidMountEffect(() => {
        displayMarker();
    }, [displayDeliveries, selectedDisplayDeliveryId, preview]);

    useEffect(() => {
        const deliveriesInPreview = preview.flatMap(
            (deliveryGroup) => deliveryGroup.deliveriesInGroup
        );

        const markerDisplayDeliveriesId = markerDisplayDeliveries.flatMap(
            (markerDisplayDelivery) =>
                markerDisplayDelivery.deliveries.map(
                    (deliveryInGroup) => deliveryInGroup.id
                )
        );

        const selectedDeliveries = deliveriesInPreview.filter(
            (deliveryInDeliveryGroup) => {
                return markerDisplayDeliveriesId.includes(deliveryInDeliveryGroup.id);
            }
        );

        const currentSelectedDeliveryMarkerIndexTemp: number[] = [];
        markerDisplayDeliveries.forEach((markerDelivery, markerDeliveryIndex) => {
            if (
                markerDelivery.deliveries.some((delivery) =>
                    selectedDeliveries.some(
                        (selectedDelivery) => selectedDelivery.id === delivery.id
                    )
                )
            ) {
                currentSelectedDeliveryMarkerIndexTemp.push(markerDeliveryIndex);
            }
        });

        let selectedDeliveryMarkerIndex: number[] = [];
        if (
            prevSelectedDeliveryMarkerIndex.length <
            currentSelectedDeliveryMarkerIndexTemp.length
        ) {
            selectedDeliveryMarkerIndex = currentSelectedDeliveryMarkerIndexTemp;
        } else if (currentSelectedDeliveryMarkerIndexTemp.length === 0) {
            selectedDeliveryMarkerIndex = prevSelectedDeliveryMarkerIndex;
        } else {
            selectedDeliveryMarkerIndex = prevSelectedDeliveryMarkerIndex;
        }

        if (currentSelectedDeliveryMarkerIndexTemp.length !== 0) {
            prevSelectedDeliveryMarkerIndex = selectedDeliveryMarkerIndex;
        } else {
            prevSelectedDeliveryMarkerIndex = [];
        }

        selectedDeliveryMarkerIndex?.forEach((markerIndex) => {
            if (markerDisplayDeliveries[markerIndex]?.deliveries) {
                const markerFormat = getMarkerFormat(
                    markerDisplayDeliveries[markerIndex].deliveries,
                    markerDisplayDeliveries[markerIndex].locationId,
                    markerDisplayDeliveriesType[markerIndex]
                );

                markers[markerIndex].setIcon({
                    content: renderToStaticMarkup(
                        <ScheduleCreatePin
                            timeRange={markerFormat.timeRange}
                            aliasLabel={markerFormat.aliasLabel}
                            totalDeliveries={markerFormat.totalDeliveries}
                            checkedDeliveries={markerFormat.checkedDeliveries}
                            focus={markerFormat.focus}
                            deliveryGroupOrder={markerFormat.deliveryGroupOrder}
                            type={markerFormat.type}
                        />
                    ),
                    anchor: new Point(1.0, 0.5)
                });
            }
        });
    }, [
        markers,
        markerDisplayDeliveries,
        preview.flatMap((deliveryGroup) => deliveryGroup.deliveriesInGroup).length,
    ]);

    useEffect(() => {
        const checkboxGroup =
            document.querySelectorAll<HTMLInputElement>(".input-checkbox");
        if (checkboxGroup) {
            checkboxGroup.forEach((e) => {
                e.checked = preview
                    .map((deliveryGroupCard) => deliveryGroupCard.deliveriesInGroup)
                    .flat()
                    .some((deliveryInGroup) => deliveryInGroup.id === e.value);
            });
        }
    }, [currentMarker, preview]);

    const mapStyle = {
        width: "100%",
        height: "100%",
    };

    return (
        <>
            <div id="map" style={mapStyle}/>
        </>
    );
};

export default ScheduleCreateMap;
